import Phaser from 'phaser';
import { MAP_GRID, MAP_DECORATIONS, BASE_POSITIONS, SPAWN_POSITIONS, getActiveMap } from '@/map/MapData';
import { TILE_SIZE, TileType, MAP_COLS, MAP_ROWS, GAME_WIDTH, GAME_HEIGHT, CANVAS_WIDTH, CANVAS_HEIGHT, BASE_HP, BASE_UPGRADE_HP, WAVE_CLEAR_BONUS, ENEMY_DAMAGE_MULTIPLIER, DIFFICULTY_REWARD_MULT, DecorType, EnemyType, EnemyWeaponType, FLYING_ENEMY_TYPES } from '@/config/GameConfig';
import { ComponentType } from '@/components/ComponentDefs';
import { PlayerBase } from '@/entities/GameBase';
import { WaveManager } from '@/systems/WaveManager';
import { RobotManager } from '@/systems/RobotManager';
import { Robot } from '@/entities/Robot';
import { Enemy } from '@/entities/Enemy';
import { EventBus, GameEvents } from '@/events/EventBus';
import { confirmPendingCompletions, rollbackPendingCompletions } from '@/cinematic/CinematicState';
import { ProjectileManager } from '@/systems/ProjectileManager';
import { resetBehaviorCaches, cleanupRobotState } from '@/behaviors/BehaviorEngine';
import { SoundManager, MusicTrack, MECHANICAL_ENEMY_TYPES, EngineSoundEntity } from '@/systems/SoundManager';
import { JuiceEffects } from '@/systems/JuiceEffects';
import { FogOfWarSystem } from '@/systems/FogOfWarSystem';
import { SaveManager } from '@/systems/SaveManager';
import { ProgressionManager } from '@/systems/ProgressionManager';
import { ResourceManager } from '@/systems/ResourceManager';
import { EnemyProjectileManager } from '@/systems/EnemyProjectileManager';
import { DesignStore } from '@/systems/DesignStore';
import { GOD } from '@/config/GodMode';
import { devDestroyMode, devMoveMode, devNukeMode } from '@/systems/DevConsole';
import { ProgressionState } from '@/systems/ProgressionManager';
import { SmartCamera } from '@/systems/SmartCamera';
import { SpatialHash } from '@/systems/SpatialHash';
import { VFXPool } from '@/systems/ObjectPool';
import { MineManager } from '@/systems/MineManager';
import { CPUPlayer } from '@/systems/CPUPlayer';
import { ScoreManager } from '@/systems/ScoreManager';
import { ImpactTimeSystem } from '@/systems/ImpactTimeSystem';
import { SlowmoFXManager } from '@/systems/SlowmoFXManager';
import { CrackedTileManager } from '@/systems/CrackedTileManager';
import { MiniProfiler } from '@/systems/MiniProfiler';
import { AchievementManager } from '@/systems/AchievementManager';
import { UITargetRegistry } from '@/cinematic/UITargetRegistry';
import { ConditionRegistry } from '@/cinematic/ConditionRegistry';
import { findFirstPlacementTile } from '@/cinematic/CameraDirector';

export enum GamePhase {
  WORKSHOP = 'workshop',
  WAVE = 'wave',
  INTERMISSION = 'intermission',
}

export class GameScene extends Phaser.Scene {
  public waveManager!: WaveManager;
  public robotManager!: RobotManager;
  public playerBase!: PlayerBase;
  public phase: GamePhase = GamePhase.WORKSHOP;
  public resources: number = 100;
  public projectileManager!: ProjectileManager;
  public gameSpeed: number = 0.5;
  private fogOfWar: FogOfWarSystem | null = null;
  public enemiesKilled: number = 0;
  /** Per-type kill counter for this mission (merged into progression on game-over) */
  public killsByType: Record<string, number> = {};
  public killsByTypeGrade: Record<string, number> = {};
  private enemyProjectileManager!: EnemyProjectileManager;
  private killStreakCount: number = 0;
  private killStreakTimer: number = 0;
  public maxKillStreak: number = 0;  // best streak this wave (for end-of-wave bonus)
  private static readonly KILL_STREAK_TIMEOUT = 2000; // ms before streak resets
  private static readonly KILL_STREAK_THRESHOLDS = [3, 5, 10, 15, 20];
  // Wave-end bonus: % repair/recharge per combo tier (capped at 20%)
  private static readonly BONUS_PER_STREAK = 0.04; // 4% per best streak level (x3=12%, x5=20%)

  // Dynamic camera state (smooth zoom to revealed area / action density)
  private camTargetZoom: number = 1;
  private camTargetX: number = 0;
  private camTargetY: number = 0;
  private camCurrentZoom: number = 1;
  private camCurrentX: number = 0;
  private camCurrentY: number = 0;
  private repairSequenceActive: boolean = false; // locks dynamic camera during repair pan
  private cameraFollowRobot: Robot | null = null; // lock-on camera follow target
  private onRobotDestroyedCleanup: ((robot: { id: string }) => void) | null = null;
  private smartCamera!: SmartCamera;
  public mineManager!: MineManager;
  public crackedTiles!: CrackedTileManager;
  private cpuPlayer!: CPUPlayer;

  // Manual camera control (DEPLOY/WORKSHOP phases)
  private manualOffsetX: number = 0;   // px offset from auto-framing center
  private manualOffsetY: number = 0;
  private manualZoomStep: number = 10;  // index into ZOOM_STEPS (default=10 → 1.0x)
  private zoomWheelAccum: number = 0;   // accumulated wheel delta for smooth trackpad zoom
  private static readonly ZOOM_STEPS = [0.5, 0.55, 0.6, 0.65, 0.7, 0.75, 0.8, 0.85, 0.9, 0.95, 1.0, 1.05, 1.1, 1.15, 1.2, 1.3, 1.4, 1.5, 1.7];
  private static readonly PAN_SPEED = 200; // px/s for keyboard pan
  private panKeys = { up: false, down: false, left: false, right: false };
  // Drag-to-pan (right-click or middle-click)
  private isDraggingCamera = false;
  private dragStartX = 0;
  private dragStartY = 0;
  /** Individual tile/decor images — batchable by texture, no preFX overhead */
  private tileImages: Phaser.GameObjects.Image[] = [];
  /** Tile images indexed by "col,row" for runtime tile updates (mud conversion) */
  private tileImageMap = new Map<string, Phaser.GameObjects.Image>();
  /** Scheduled mud revert timers (col,row → original tile type) */
  private mudRevertTimers: Phaser.Time.TimerEvent[] = [];
  private decorImages: Phaser.GameObjects.Image[] = [];
  private decorExtras: Phaser.GameObjects.GameObject[] = [];
  private resImages: Phaser.GameObjects.Image[] = [];

  /** Resource mining HP bars — keyed by "col,row", auto-created on first DECOR_MINING event */
  private resourceHpBars = new Map<string, { bg: Phaser.GameObjects.Rectangle; bar: Phaser.GameObjects.Rectangle; fadeTimer: number }>();

  // ── Cached per-frame target arrays (reused to avoid GC pressure) ──
  private _cachedRobotTargets: { x: number; y: number; alive: boolean; isFlying: boolean; isCamoActive: boolean; getExposedTarget: () => { x: number; y: number } | null }[] = [];
  private _cachedGroundTargets: typeof GameScene.prototype._cachedRobotTargets = [];
  private _cachedHitTargets: { x: number; y: number; alive: boolean; takeDamage: (d: number, ax?: number, ay?: number) => void; collisionRadius: number }[] = [];
  private _cachedEnemyHitTargets: { x: number; y: number; alive: boolean; takeDamage: (d: number) => void; collisionRadius?: number }[] = [];
  private _hitHash = new SpatialHash<typeof GameScene.prototype._cachedHitTargets[0]>(60);

  constructor() {
    super({ key: 'GameScene' });
  }

  create(): void {
    // Hook shutdown to scene stop event so cleanup runs on scene.stop() / restart
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);

    // Initialize VFX object pools (must be before any spawning)
    VFXPool.init(this);

    // Create player base with upgrade levels (campaign persists, quick play separate)
    const baseUpgradeLevels = this.registry.get('currentLevelNode')
      ? ProgressionManager.getBaseUpgradeLevels()
      : (this.registry.get('qpBaseUpgradeLevels') as Record<string, number>) ?? {};
    this.playerBase = new PlayerBase(this, BASE_POSITIONS, baseUpgradeLevels);

    // Campaign: resources = Treasury (persistent currency)
    // Quick play: use tier-based starting resources
    this.resources = this.registry.get('currentLevelNode')
      ? ProgressionManager.getCurrency()
      : (this.registry.get('startingResources') as number) ?? 35;

    // Challenge mode: Co/Electro start at 0 and don't persist
    if (!this.isCampaign) {
      ResourceManager.enterTemporary();
    }

    // Set DesignStore mode so designs are separated between campaign and challenge
    DesignStore.mode = this.isCampaign ? 'campaign' : 'challenge';

    this.phase = GamePhase.WORKSHOP;

    // Snapshot state at level start (for rollback on restart/quit/defeat)
    this.registry.set('levelStartSnapshot', {
      progression: ProgressionManager.getState(),
      resources: ResourceManager.getState(),
      designs: DesignStore.getAll().map(d => ({ ...d, design: { ...d.design } })),
    });

    // Persist snapshot to localStorage for crash recovery (campaign only)
    if (this.isCampaign) {
      ProgressionManager.savePreGameSnapshot();
    }

    // Play level music based on map index (cycles 1→5 then repeats)
    const levelNodeForMusic = this.registry.get('currentLevelNode') as { index?: number } | undefined;
    if (levelNodeForMusic?.index != null) {
      SoundManager.playLevelMusicForMap(levelNodeForMusic.index);
    } else {
      SoundManager.playRandomLevelMusic();
    }

    // Clear map-dependent caches from previous games
    resetBehaviorCaches();

    // Camera: initial zoom to fit full map (will be adjusted dynamically by fog)
    const cam = this.cameras.main;
    cam.setBackgroundColor('#000000'); // black outside map, matches fog of war
    cam.removeBounds(); // no bounds clamping — dynamic camera handles framing
    const maxZoom = Math.min(CANVAS_WIDTH / GAME_WIDTH, CANVAS_HEIGHT / GAME_HEIGHT);
    cam.setZoom(maxZoom);
    // Initialize camera on the map center (will be refined by dynamic camera on first frame)
    cam.centerOn(GAME_WIDTH / 2, GAME_HEIGHT / 2);
    this.camTargetZoom = maxZoom;
    this.camTargetX = GAME_WIDTH / 2;
    this.camTargetY = GAME_HEIGHT / 2;
    this.camCurrentZoom = maxZoom;
    this.camCurrentX = GAME_WIDTH / 2;
    this.camCurrentY = GAME_HEIGHT / 2;

    this.renderMap();
    this.applyVisualFX();

    // Listen for live GOD param changes to update tile saturation
    EventBus.on(GameEvents.GOD_PARAMS_CHANGED, this.applyVisualFX, this);

    // Clean up per-robot behavior state when a robot is destroyed
    this.onRobotDestroyedCleanup = (robot: { id: string }) => cleanupRobotState(robot.id);
    EventBus.on(GameEvents.ROBOT_DESTROYED, this.onRobotDestroyedCleanup);

    // Camera lock-on: follow selected robot, release on null
    EventBus.on(GameEvents.ROBOT_SELECTED, (robot: Robot | null) => {
      this.cameraFollowRobot = robot?.alive ? robot : null;
      if (this.cameraFollowRobot) {
        // Reset pan offsets so camera centers on the robot, not robot + old offset
        this.manualOffsetX = 0;
        this.manualOffsetY = 0;
      }
    });

    // Cracked tiles: distant placement tiles start damaged
    this.crackedTiles = new CrackedTileManager(this);
    this.crackedTiles.init();

    this.waveManager = new WaveManager(this);
    // Set tier for wave composition (campaign uses node tier, challenge defaults to 0)
    const activeMap = getActiveMap();
    const levelNode = this.registry.get('currentLevelNode') as { tier: number; index?: number; waves?: number } | undefined;
    // Stress test maps use high tier (all grades + big power budget); normal challenge = tier 0
    const isStressTest = !!(activeMap.predeployedRobots && activeMap.predeployedRobots.length > 0);
    const tier = levelNode?.tier ?? (isStressTest ? 30 : 0);
    const mapIdx = levelNode?.index ?? (isStressTest ? 99 : undefined);
    // Challenge mode: use map's waveCount; Campaign: use node's waves or WaveComposer default
    const overrideWaves = levelNode?.waves ?? activeMap.waveCount;
    // intensity: 0=Trivial(0.3x), 1=Easy(0.6x), 2=Normal(1x), 3=Hard(1.8x), 4=Brutal(3x)
    const intensityMults = [0.3, 0.6, 1, 1.8, 3];
    const intensityMult = intensityMults[activeMap.intensity ?? 2] ?? 1;
    this.waveManager.setTier(tier, mapIdx, overrideWaves, activeMap.flatWaveDistribution, activeMap.spawnDelayMs, activeMap.allowedEnemies, intensityMult, activeMap.allowedGrades);
    // Create destructible enemy bases at spawn points
    this.waveManager.createBases(tier);
    this.robotManager = new RobotManager(this);
    // Wire up robot provider for fuzzy threat-aware path selection
    this.waveManager.setRobotProvider(() => this.robotManager.robots.filter(r => r.alive));

    // Auto-deploy pre-placed robots (stress test / benchmark maps)
    if (isStressTest) {
      for (const preset of activeMap.predeployedRobots!) {
        const rx = preset.col * TILE_SIZE + TILE_SIZE / 2;
        const ry = preset.row * TILE_SIZE + TILE_SIZE / 2;
        const robot = new Robot(this, rx, ry);
        for (const comp of preset.components) {
          robot.addComponent(comp.type, comp.lx, comp.ly, comp.rotation ?? 0, comp.flipX ?? false, comp.flipY ?? false, comp.evoLevel ?? 0);
        }
        // Set heading BEFORE resetHpEnergy so stability uses correct flight axis
        const dirComp = preset.components.find(c => c.type === ComponentType.DIRECTION);
        if (dirComp) {
          const rot = dirComp.rotation ?? 0;
          robot.heading = rot;
          robot.buildHeading = rot;
          robot.recalcStats();
        }
        robot.resetHpEnergy();
        robot.normalizeSize();
        robot.placed = true;
        this.robotManager.addRobot(robot, preset.rules);
      }
    }

    this.projectileManager = new ProjectileManager(this);
    this.projectileManager.setEnemyBases(this.waveManager.enemyBases);
    // Fog of war only in Infernal difficulty (mult = 3.0)
    if (ENEMY_DAMAGE_MULTIPLIER >= 3.0) {
      this.fogOfWar = new FogOfWarSystem(this);
    }
    this.enemyProjectileManager = new EnemyProjectileManager(this);
    this.mineManager = new MineManager(this);
    this.cpuPlayer = new CPUPlayer(this);
    this.cpuPlayer.init();
    this.smartCamera = new SmartCamera();
    this.smartCamera.initHeatmap(this);
    ImpactTimeSystem.init(this.smartCamera);
    SlowmoFXManager.init(this);

    // Initialize ScoreManager with map config
    ScoreManager.reset({
      totalWaves: this.waveManager.totalWaves,
      tier,
      mapCols: MAP_COLS,
      mapRows: MAP_ROWS,
      tileSize: TILE_SIZE,
      enemyBasesTotal: this.waveManager.enemyBasesTotal,
      enemyBasesTotalHp: this.waveManager.enemyBases.reduce((sum, b) => sum + b.maxHp, 0),
      playerNodesTotal: this.playerBase.totalNodes,
      startingResources: this.resources,
    });

    // Listen to events
    this.enemiesKilled = 0;
    this.killsByType = {};
    this.killsByTypeGrade = {};
    EventBus.on(GameEvents.ENEMY_REACHED_BASE, this.onEnemyReachedBase, this);
    EventBus.on(GameEvents.WAVE_COMPLETED, this.onWaveCompleted, this);
    EventBus.on(GameEvents.ENEMY_DESTROYED, this.onEnemyDestroyed, this);
    EventBus.on(GameEvents.GAME_OVER, this.onGameOver, this);
    EventBus.on(GameEvents.ENEMY_FIRED, this.onEnemyFired, this);
    EventBus.on(GameEvents.COIN_ARRIVED, this.onCoinArrived, this);
    EventBus.on(GameEvents.RESOURCE_ARRIVED, this.onResourceArrived, this);

    // Excavator: mining HP bar + destroy decor sprite + resource popup on harvest
    EventBus.on(GameEvents.DECOR_MINING, this.onDecorMining, this);
    EventBus.on(GameEvents.DECOR_HARVESTED, this.onDecorHarvested, this);
    EventBus.on(GameEvents.MUD_TILE_CONVERTED, this.onMudTileConverted, this);

    // Connect visual juice effects to game events
    JuiceEffects.connectToEvents(this);

    // Expose managers to UI scene via registry
    this.registry.set('waveManager', this.waveManager);
    this.registry.set('robotManager', this.robotManager);
    this.registry.set('gameScene', this);
    this.registry.set('projectileManager', this.projectileManager);
    this.registry.set('playerBase', this.playerBase);
    this.registry.set('mineManager', this.mineManager);
    this.registry.set('crackedTiles', this.crackedTiles);

    // Delay initial events so UIScene has time to create and register listeners
    this.time.delayedCall(0, () => {
      EventBus.emit(GameEvents.RESOURCES_CHANGED, this.resources);
      EventBus.emit(GameEvents.PHASE_CHANGED, this.phase);
    });

    // ── Manual camera controls (DEPLOY/WORKSHOP) ──
    this.setupManualCameraControls();

    // ── Nuke explosion animation (spritesheet 4x2 = 8 frames, 5s total) ──
    if (!this.anims.exists('nuke_boom')) {
      this.anims.create({
        key: 'nuke_boom',
        frames: this.anims.generateFrameNumbers('nuke_explosion', { start: 0, end: 7 }),
        frameRate: 8 / 2, // 8 frames over 2 seconds
        repeat: 0,
      });
    }

    // ── Acid projectile animation (spritesheet 4x1 = 4 frames) ──
    if (!this.anims.exists('acid_fly')) {
      this.anims.create({
        key: 'acid_fly',
        frames: this.anims.generateFrameNumbers('acid', { start: 0, end: 3 }),
        frameRate: 10,
        repeat: -1,
      });
    }

    // ── Dev console interactive modes (destroy / move / nuke) ──
    this.setupDevModes();

    // Register placement tile for cinematic spotlight targeting (world coords)
    UITargetRegistry.register('placementTile', () => {
      const tile = findFirstPlacementTile();
      if (!tile) return null;
      return { x: tile.x - TILE_SIZE / 2, y: tile.y - TILE_SIZE / 2, w: TILE_SIZE, h: TILE_SIZE };
    });

    // ── Multi-target registrations (for multi-highlight spotlight steps) ──
    const findTileRects = (...types: TileType[]) => {
      const typeSet = new Set(types);
      const rects: Array<{ x: number; y: number; w: number; h: number }> = [];
      for (let r = 0; r < MAP_ROWS; r++) {
        for (let c = 0; c < MAP_COLS; c++) {
          if (typeSet.has(MAP_GRID[r]?.[c])) {
            rects.push({ x: c * TILE_SIZE, y: r * TILE_SIZE, w: TILE_SIZE, h: TILE_SIZE });
          }
        }
      }
      return rects;
    };

    UITargetRegistry.registerMulti('allPlacementTiles', () => {
      const rects = findTileRects(TileType.PLACEMENT, TileType.GRILLE);
      return rects.length > 0 ? rects : null;
    });
    UITargetRegistry.registerMulti('allPathTiles', () => {
      const rects = findTileRects(TileType.PATH);
      return rects.length > 0 ? rects : null;
    });
    UITargetRegistry.registerMulti('allWalls', () => {
      const rects = findTileRects(TileType.WALL);
      return rects.length > 0 ? rects : null;
    });
    UITargetRegistry.registerMulti('allSpecialTiles', () => {
      const rects = findTileRects(TileType.MUD, TileType.WATER, TileType.MAGMA);
      return rects.length > 0 ? rects : null;
    });
    UITargetRegistry.registerMulti('allBrokenPlacements', () => {
      const rects = findTileRects(TileType.BROKEN_PLACEMENT);
      return rects.length > 0 ? rects : null;
    });

    // ── Dynamic entity targets (resolved at runtime, world-space) ──
    const self = this;
    const enemyRect = (e: { body: { x: number; y: number; displayWidth: number; displayHeight: number } }) => {
      const hw = e.body.displayWidth / 2;
      const hh = e.body.displayHeight / 2;
      return { x: e.body.x - hw, y: e.body.y - hh, w: e.body.displayWidth, h: e.body.displayHeight };
    };
    const robotRect = (r: { x: number; y: number }) => {
      const s = TILE_SIZE;
      return { x: r.x - s / 2, y: r.y - s / 2, w: s, h: s };
    };

    // All enemies (alive)
    UITargetRegistry.registerMulti('allEnemies', () => {
      const rects = self.waveManager.enemies.filter(e => e.alive).map(enemyRect);
      return rects.length > 0 ? rects : null;
    });

    // Enemies by type
    const enemyTypes: EnemyType[] = [
      EnemyType.GRUNT, EnemyType.RUNNER, EnemyType.TANK, EnemyType.SWARM,
      EnemyType.BRUTE, EnemyType.PHANTOM, EnemyType.DRONE, EnemyType.BOMBER,
      EnemyType.WASP, EnemyType.SPECTER,
    ];
    for (const et of enemyTypes) {
      const key = `all${et.charAt(0).toUpperCase() + et.slice(1)}s`;
      UITargetRegistry.registerMulti(key, () => {
        const rects = self.waveManager.enemies
          .filter(e => e.alive && e.enemyType === et)
          .map(enemyRect);
        return rects.length > 0 ? rects : null;
      });
    }

    // All robots (alive)
    UITargetRegistry.registerMulti('allRobots', () => {
      const rects = self.robotManager.robots.filter(r => r.alive).map(robotRect);
      return rects.length > 0 ? rects : null;
    });

    // Base positions
    UITargetRegistry.registerMulti('allBases', () => {
      return BASE_POSITIONS.map(p => ({
        x: p.x - TILE_SIZE / 2, y: p.y - TILE_SIZE / 2, w: TILE_SIZE, h: TILE_SIZE,
      }));
    });

    // Spawn positions
    UITargetRegistry.registerMulti('allSpawns', () => {
      return SPAWN_POSITIONS.map(p => ({
        x: p.x - TILE_SIZE / 2, y: p.y - TILE_SIZE / 2, w: TILE_SIZE, h: TILE_SIZE,
      }));
    });

    // ── Condition registrations (for skipIf on cinematic steps) ──
    ConditionRegistry.register('noWalls', () => findTileRects(TileType.WALL).length === 0);
    ConditionRegistry.register('noBrokenPlacements', () => findTileRects(TileType.BROKEN_PLACEMENT).length === 0);
    ConditionRegistry.register('noSpecialTiles', () => findTileRects(TileType.MUD, TileType.WATER, TileType.MAGMA).length === 0);
    ConditionRegistry.register('noEnemies', () => self.waveManager.enemies.filter(e => e.alive).length === 0);
    ConditionRegistry.register('noRobots', () => self.robotManager.robots.filter(r => r.alive).length === 0);
  }

  private setupManualCameraControls(): void {
    const kb = this.input.keyboard!;

    // Pan: arrows + WASD (keydown/keyup tracking for continuous movement)
    const bind = (keys: string[], dir: keyof typeof this.panKeys) => {
      for (const k of keys) {
        kb.on(`keydown-${k}`, () => { this.panKeys[dir] = true; });
        kb.on(`keyup-${k}`, () => { this.panKeys[dir] = false; });
      }
    };
    bind(['UP', 'W'], 'up');
    bind(['DOWN', 'S'], 'down');
    bind(['LEFT', 'A'], 'left');
    bind(['RIGHT', 'D'], 'right');

    // Zoom: mouse wheel — zoom toward/away from mouse position
    // Accumulates small trackpad deltas so it takes real intent to change a step
    const ZOOM_WHEEL_THRESHOLD = 60; // accumulated pixels before changing a zoom step
    this.input.on('wheel', (p: Phaser.Input.Pointer, _gx: Phaser.GameObjects.GameObject[], _dx: number, dy: number) => {
      if (this.registry.get('cinematicInputLock')) return;
      this.zoomWheelAccum += dy;
      if (Math.abs(this.zoomWheelAccum) < ZOOM_WHEEL_THRESHOLD) return;

      const oldStep = this.manualZoomStep;
      // Consume one step per threshold crossed
      if (this.zoomWheelAccum >= ZOOM_WHEEL_THRESHOLD && this.manualZoomStep > 0) {
        this.manualZoomStep--;
        this.zoomWheelAccum -= ZOOM_WHEEL_THRESHOLD;
      } else if (this.zoomWheelAccum <= -ZOOM_WHEEL_THRESHOLD && this.manualZoomStep < GameScene.ZOOM_STEPS.length - 1) {
        this.manualZoomStep++;
        this.zoomWheelAccum += ZOOM_WHEEL_THRESHOLD;
      }
      if (this.manualZoomStep === oldStep) { this.zoomWheelAccum = 0; return; }

      // Adjust offset so the world point under the mouse stays fixed
      const oldZoom = this.camCurrentZoom;
      const newZoom = oldZoom * (GameScene.ZOOM_STEPS[this.manualZoomStep] / GameScene.ZOOM_STEPS[oldStep]);
      const worldX = this.camCurrentX + (p.x - CANVAS_WIDTH / 2) / oldZoom;
      const worldY = this.camCurrentY + (p.y - CANVAS_HEIGHT / 2) / oldZoom;
      const newCamX = worldX - (p.x - CANVAS_WIDTH / 2) / newZoom;
      const newCamY = worldY - (p.y - CANVAS_HEIGHT / 2) / newZoom;
      this.manualOffsetX += newCamX - this.camCurrentX;
      this.manualOffsetY += newCamY - this.camCurrentY;
    });

    // Zoom: +/- keys
    kb.on('keydown-PLUS', () => {
      if (this.registry.get('cinematicInputLock')) return;
      if (this.manualZoomStep < GameScene.ZOOM_STEPS.length - 1) this.manualZoomStep++;
    });
    kb.on('keydown-MINUS', () => {
      if (this.registry.get('cinematicInputLock')) return;
      if (this.manualZoomStep > 0) this.manualZoomStep--;
    });

    // Reset: R key → return to auto-framing
    kb.on('keydown-R', () => {
      if (this.registry.get('cinematicInputLock')) return;
      this.manualOffsetX = 0;
      this.manualOffsetY = 0;
      this.manualZoomStep = 10; // index 10 = 1.0x (neutral)
    });

    // Initialize zoom step to neutral (1.0x)
    this.manualZoomStep = 10;

    // Pan: drag on any empty area (left/right/middle click)
    // Left-click uses a distance threshold to distinguish drag from click
    const CAM_DRAG_THRESHOLD = 5; // px before left-click becomes a drag
    let pendingLeftDrag = false;
    let leftDragOriginX = 0;
    let leftDragOriginY = 0;

    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      if (this.registry.get('cinematicInputLock')) return;
      // Don't start camera drag when dev move mode is grabbing an entity
      if (devMoveMode && this.devDragTarget) return;

      if (p.rightButtonDown() || p.middleButtonDown()) {
        // Right/middle: immediate drag (works in all phases including WAVE)
        this.isDraggingCamera = true;
        this.dragStartX = p.x;
        this.dragStartY = p.y;
        const canvas = this.game.canvas;
        if (canvas) canvas.style.cursor = 'grabbing';
      } else if (p.leftButtonDown()) {
        if (this.phase === GamePhase.WAVE) return; // left-click during WAVE is for robot selection
        // Left: only start drag if pointer is not over an interactive object
        // and UIScene didn't consume this click (robot selection)
        if (this.registry.get('uiConsumedClick')) return;
        const hitList = this.input.hitTestPointer(p);
        if (hitList.length === 0) {
          pendingLeftDrag = true;
          leftDragOriginX = p.x;
          leftDragOriginY = p.y;
          this.dragStartX = p.x;
          this.dragStartY = p.y;
        }
      }
    });

    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      // Don't drag camera while dev move mode is dragging an entity
      if (devMoveMode && this.devDragTarget) return;
      // Promote pending left-click to drag once threshold is exceeded
      if (pendingLeftDrag && !this.isDraggingCamera) {
        const dist = Math.hypot(p.x - leftDragOriginX, p.y - leftDragOriginY);
        if (dist >= CAM_DRAG_THRESHOLD) {
          this.isDraggingCamera = true;
          const canvas = this.game.canvas;
          if (canvas) canvas.style.cursor = 'grabbing';
        }
      }
      if (!this.isDraggingCamera) return;
      // If following a robot, break the lock-on when user drags the camera
      if (this.cameraFollowRobot) {
        this.cameraFollowRobot = null;
        this.cameras.main.roundPixels = true; // restore pixel snapping
        this.manualOffsetX = 0;
        this.manualOffsetY = 0;
        // Keep manualZoomStep — player expects zoom level to persist
        EventBus.emit(GameEvents.ROBOT_SELECTED, null);
      }
      const dx = p.x - this.dragStartX;
      const dy = p.y - this.dragStartY;
      this.manualOffsetX -= dx / this.camCurrentZoom;
      this.manualOffsetY -= dy / this.camCurrentZoom;
      this.dragStartX = p.x;
      this.dragStartY = p.y;
    });

    this.input.on('pointerup', () => {
      pendingLeftDrag = false;
      if (this.isDraggingCamera) {
        this.isDraggingCamera = false;
        const canvas = this.game.canvas;
        if (canvas) canvas.style.cursor = 'default';
      }
    });

    // Disable browser context menu on right-click
    this.input.mouse?.disableContextMenu();
  }

  // ── Dev console interactive modes ──
  private devDragTarget: { entity: Robot | Enemy; type: 'robot' | 'enemy' } | null = null;

  private setupDevModes(): void {
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.registry.get('cinematicInputLock')) return;
      // Nuke mode: click anywhere to launch
      if (devNukeMode) {
        this.launchNuke(pointer.worldX, pointer.worldY);
        return;
      }
      if (!devDestroyMode && !devMoveMode) return;
      const worldX = pointer.worldX;
      const worldY = pointer.worldY;
      const hit = this.findEntityAt(worldX, worldY);
      if (!hit) return;

      if (devDestroyMode) {
        hit.entity.destroy();
        SoundManager.play('clickCancel');
        return;
      }

      if (devMoveMode) {
        this.devDragTarget = hit;
        const canvas = this.game.canvas;
        if (canvas) canvas.style.cursor = 'grabbing';
      }
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!devMoveMode || !this.devDragTarget || !pointer.isDown) return;
      const { entity, type } = this.devDragTarget;
      if (!entity.alive) { this.devDragTarget = null; return; }
      const wx = pointer.worldX;
      const wy = pointer.worldY;
      if (type === 'robot') {
        const r = entity as Robot;
        r.x = wx; r.y = wy;
        r.container.setPosition(wx, wy);
      } else {
        const e = entity as Enemy;
        e.body.setPosition(wx, wy);
      }
    });

    this.input.on('pointerup', () => {
      if (this.devDragTarget) {
        this.devDragTarget = null;
        const canvas = this.game.canvas;
        if (canvas && devMoveMode) canvas.style.cursor = 'grab';
      }
    });
  }

  private findEntityAt(x: number, y: number): { entity: Robot | Enemy; type: 'robot' | 'enemy' } | null {
    // Check enemies first (usually more numerous, on top)
    let bestDist = 900; // max 30px radius squared
    let best: { entity: Robot | Enemy; type: 'robot' | 'enemy' } | null = null;
    for (const e of this.waveManager.enemies) {
      if (!e.alive) continue;
      const dx = e.body.x - x, dy = e.body.y - y;
      const d2 = dx * dx + dy * dy;
      if (d2 < bestDist) { bestDist = d2; best = { entity: e, type: 'enemy' }; }
    }
    for (const r of this.robotManager.robots) {
      if (!r.alive) continue;
      const dx = r.x - x, dy = r.y - y;
      const d2 = dx * dx + dy * dy;
      if (d2 < bestDist) { bestDist = d2; best = { entity: r, type: 'robot' }; }
    }
    return best;
  }

  // ── Nuke Strike ──

  private static readonly NUKE_RADIUS = TILE_SIZE * 8;        // kill radius in px
  private static readonly NUKE_PROJECTILE_SPEED = 120;        // px/s (slow lob)
  private static readonly NUKE_LAUNCH_HEIGHT = 400;           // px above target

  private launchNuke(targetX: number, targetY: number): void {
    const startX = targetX;
    const startY = targetY - GameScene.NUKE_LAUNCH_HEIGHT;

    // Create a small bright projectile (simple circle)
    const proj = this.add.circle(startX, startY, 5, 0xffaa00, 1).setDepth(100);
    // Trail glow
    const glow = this.add.circle(startX, startY, 10, 0xff6600, 0.4).setDepth(99);

    const dist = GameScene.NUKE_LAUNCH_HEIGHT;
    const duration = (dist / GameScene.NUKE_PROJECTILE_SPEED) * 1000;

    // Animate projectile falling to target with slight arc
    this.tweens.add({
      targets: [proj, glow],
      y: targetY,
      duration,
      ease: 'Quad.easeIn',
      onUpdate: () => {
        glow.setPosition(proj.x, proj.y);
      },
      onComplete: () => {
        proj.destroy();
        glow.destroy();
        this.detonateNuke(targetX, targetY);
      },
    });
  }

  private detonateNuke(x: number, y: number): void {
    // ── 1. Mega camera flash + shake ──
    JuiceEffects.cameraFlash(this, 0xffffff, 600, 0.8);
    JuiceEffects.screenShake(this, 0.02, 800);

    // ── 2. Expanding white shockwave ring ──
    const ring = this.add.circle(x, y, 10, 0xffffff, 0.6).setDepth(101);
    ring.setStrokeStyle(4, 0xffcc00, 0.8);
    const ringState = { r: 10 };
    this.tweens.add({
      targets: ringState,
      r: GameScene.NUKE_RADIUS,
      duration: 800,
      ease: 'Quad.easeOut',
      onUpdate: () => {
        ring.setRadius(ringState.r);
        ring.setAlpha(1 - ringState.r / GameScene.NUKE_RADIUS);
      },
      onComplete: () => ring.destroy(),
    });

    // ── 3. Nuke explosion spritesheet animation ──
    const explosion = this.add.sprite(x, y, 'nuke_explosion').setDepth(102);
    // 20% of nuke radius
    const targetSize = GameScene.NUKE_RADIUS * 0.6;
    explosion.setDisplaySize(targetSize, targetSize * (243 / 256));
    explosion.play('nuke_boom');

    // On last frame: fade out smoothly
    explosion.on(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
      this.tweens.add({
        targets: explosion,
        alpha: 0,
        duration: 1500,
        ease: 'Quad.easeIn',
        onComplete: () => explosion.destroy(),
      });
    });

    // ── 4. Kill all enemies + enemy bases in radius ──
    const radiusSq = GameScene.NUKE_RADIUS * GameScene.NUKE_RADIUS;
    for (const e of this.waveManager.enemies) {
      if (!e.alive) continue;
      const dx = e.body.x - x, dy = e.body.y - y;
      if (dx * dx + dy * dy <= radiusSq) {
        e.destroy();
      }
    }
    for (const b of this.waveManager.enemyBases) {
      if (!b.alive) continue;
      const dx = b.x - x, dy = b.y - y;
      if (dx * dx + dy * dy <= radiusSq) {
        b.takeDamage(b.hp);
      }
    }

    // Sound effect
    SoundManager.play('explosion');
  }

  /** Convert a darken alpha (0=no darkening, 1=fully black) to a tint color */
  private static darkenTint(alpha: number): number {
    const b = Math.round((1 - alpha) * 255);
    return (b << 16) | (b << 8) | b;
  }

  private static readonly TILE_TEXTURES: Record<TileType, string> = {
    [TileType.GROUND]:    'tile_gazon',
    [TileType.WALL]:      'tile_mur',
    [TileType.PATH]:      'tile_goudron',
    [TileType.PLACEMENT]: 'tile_placement',
    [TileType.BASE]:      'tile_electro',
    [TileType.SPAWN]:     'tile_electro',
    [TileType.MUD]:       'tile_boue',
    [TileType.WATER]:     'tile_eau',
    [TileType.GRILLE]:    'tile_grille',
    [TileType.MAGMA]:     'tile_magma',
    [TileType.BROKEN_PLACEMENT]: 'tile_broken_placement',
  };

  private renderMap(): void {
    // ── Individual images per tile — batchable by texture (~10 draw calls) ──
    // No preFX per image, no dim rectangles. Darkening via tint.
    const darkenTint = GameScene.darkenTint(GOD.tileDarken);
    for (let row = 0; row < MAP_ROWS; row++) {
      for (let col = 0; col < MAP_COLS; col++) {
        const tileType = MAP_GRID[row][col];
        const texKey = GameScene.TILE_TEXTURES[tileType];
        if (!texKey || !this.textures.exists(texKey)) continue;
        const img = this.add.image(
          col * TILE_SIZE + TILE_SIZE / 2,
          row * TILE_SIZE + TILE_SIZE / 2,
          texKey,
        ).setDisplaySize(TILE_SIZE, TILE_SIZE).setDepth(0).setTint(darkenTint);
        this.tileImages.push(img);
        this.tileImageMap.set(`${col},${row}`, img);
      }
    }

    // Render decorations
    this.renderDecorations();
  }

  /** Decoration type → texture key */
  private static readonly DECOR_TEXTURES: Record<DecorType, string> = {
    [DecorType.TREE]:        'decor_arbre',
    [DecorType.RUINS]:       'decor_ruines',
    [DecorType.COLUMN]:      'decor_colonne',
    [DecorType.CHASM]:       'decor_gouffre',
    [DecorType.CONDUIT]:     'decor_conduit',
    [DecorType.BARREL]:      'decor_barrel',
    [DecorType.BROKEN_WALL]: 'decor_broken_wall',
    [DecorType.CAISSE]:      'decor_caisse_0', // variants _0/_1/_2 randomized at render
    [DecorType.DEAD_TREE]:   'decor_dead_tree',
    [DecorType.FLAQUE]:      'decor_flaque',
    [DecorType.ROCHER]:      'decor_rocher',
    [DecorType.SOUCHE]:      'decor_souche',
    [DecorType.CONNARDIUM]:  'decor_connardium',
    [DecorType.GARBAGE]:     'decor_garbage',
    [DecorType.BARRIERE]:    'decor_barriere',
    [DecorType.BROKEN_STATUE]: 'decor_broken_statue',
  };

  /** Resource decoration types — tracked separately for FX */
  private static readonly RESOURCE_DECOR_TYPES = new Set([
    DecorType.CONNARDIUM, DecorType.GARBAGE, DecorType.BARREL, DecorType.CAISSE, DecorType.RUINS,
  ]);

  private renderDecorations(): void {
    if (MAP_DECORATIONS.length === 0) return;
    const decorDarken = GameScene.darkenTint(GOD.decorDarken);
    const resDarken = GameScene.darkenTint(GOD.resDarken);
    for (const decor of MAP_DECORATIONS) {
      let texKey = GameScene.DECOR_TEXTURES[decor.type];
      // Caisses: pick deterministic random variant _0/_1/_2 based on position
      if (decor.type === DecorType.CAISSE) {
        const variant = (decor.col * 7 + decor.row * 13) % 3;
        texKey = `decor_caisse_${variant}`;
      }
      if (!texKey || !this.textures.exists(texKey)) continue;
      // Skip resources on placement tiles
      const isRes = GameScene.RESOURCE_DECOR_TYPES.has(decor.type);
      const tileAtDecor = MAP_GRID[decor.row]?.[decor.col];
      if (isRes) {
        console.warn(`[DECOR] resource ${decor.type} at (${decor.col},${decor.row}) tile=${tileAtDecor}`);
        if (tileAtDecor === TileType.PLACEMENT || tileAtDecor === TileType.BROKEN_PLACEMENT) {
          console.warn(`  → SKIPPED (on placement tile)`);
          continue;
        }
      }
      const cx = decor.col * TILE_SIZE + TILE_SIZE / 2;
      const cy = decor.row * TILE_SIZE + TILE_SIZE / 2;
      // Drop shadow for crates — dark ellipse beneath the sprite
      if (decor.type === DecorType.CAISSE) {
        const sw = TILE_SIZE * 0.84;
        const sh = TILE_SIZE * 0.264;
        const shadow = this.add.ellipse(cx, cy + TILE_SIZE * 0.34, sw, sh, 0x000000, 0.3)
          .setDepth(1.49);
        this.decorExtras.push(shadow);
      }
      const img = this.add.image(cx, cy, texKey)
        .setDisplaySize(TILE_SIZE, TILE_SIZE)
        .setDepth(1.5)
        .setTint(isRes ? resDarken : decorDarken);
      if (isRes) {
        this.resImages.push(img);
      } else {
        this.decorImages.push(img);
      }
    }
  }

  /**
   * Apply per-group visual FX (saturation, brightness, hue) to tiles, decorations, and resources.
   * Each group has independent ColorMatrix FX and darken overlay rectangles.
   */
  private applyVisualFX = (): void => {
    // Apply darken tint on each image group (no preFX — avoids framebuffer clipping)
    const tileTint = GameScene.darkenTint(GOD.tileDarken);
    for (const img of this.tileImages) img.setTint(tileTint);
    // Repaired LED tiles must match the same darken level
    if (this.crackedTiles) {
      for (const img of this.crackedTiles.getRepairedImages()) img.setTint(tileTint);
    }
    const decorTint = GameScene.darkenTint(GOD.decorDarken);
    for (const img of this.decorImages) img.setTint(decorTint);
    const resTint = GameScene.darkenTint(GOD.resDarken);
    for (const img of this.resImages) img.setTint(resTint);
  };

  // ── Phase management ──

  startWave(): void {
    if (this.phase !== GamePhase.WORKSHOP) return;
    this.phase = GamePhase.WAVE;
    EventBus.emit(GameEvents.PHASE_CHANGED, this.phase);
    this.waveManager.startWave();
    SoundManager.startEngineLoops();
  }

  /** Whether this game session is a campaign mission (resources = Treasury) */
  get isCampaign(): boolean {
    return !!this.registry.get('currentLevelNode');
  }

  spendResources(amount: number): boolean {
    // During cinematic: everything is free, no deduction
    if (this.registry.get('cinematicActive')) return true;

    if (this.resources >= amount) {
      this.resources -= amount;
      ScoreManager.recordResourceSpent(amount);
      if (this.isCampaign) this.syncTreasury();
      EventBus.emit(GameEvents.RESOURCES_CHANGED, this.resources);
      return true;
    }
    return false;
  }

  /** Add resources and sync Treasury in campaign mode */
  /** Remove fog vision sources at a world position (e.g. when a robot is sold) */
  clearFogAt(px: number, py: number): void {
    this.fogOfWar?.removeSourcesAt(px, py);
  }

  addResources(amount: number): void {
    this.resources += amount;
    ScoreManager.recordResourceEarned(amount);
    if (this.isCampaign) this.syncTreasury();
    EventBus.emit(GameEvents.RESOURCES_CHANGED, this.resources);
  }

  /** Sync in-game resources back to persistent Treasury */
  private syncTreasury(): void {
    const diff = this.resources - ProgressionManager.getCurrency();
    if (diff !== 0) ProgressionManager.addCurrency(diff);
  }

  // ── Event handlers ──

  private onEnemyReachedBase = (enemy: { baseDamage?: number; body?: { x: number; y: number } }): void => {
    // Melee damage to nearest alive base node
    this.playerBase.takeDamage(enemy.baseDamage ?? 1, enemy.body?.x, enemy.body?.y);
  };

  private onWaveCompleted = (waveNum: number): void => {
    // During cinematic simulation: stop combat but skip all persistence/UI
    if (this.registry.get('cinematicSimulation')) {
      this.phase = GamePhase.INTERMISSION;
      SoundManager.stopEngineLoops();
      return;
    }
    // Cease fire immediately — no shooting during celebration/repair/overlay
    this.phase = GamePhase.INTERMISSION;
    SoundManager.stopEngineLoops();

    // Wave clear bonus (scaled by GOD mode)
    this.addResources(Math.round(WAVE_CLEAR_BONUS * GOD.resourcesMult * DIFFICULTY_REWARD_MULT));

    // 1) Immediately show "WAVE CLEARED!" celebration + sound
    EventBus.emit(GameEvents.WAVE_CELEBRATION);

    // 2) After 500ms, run repair & recharge then finalize
    this.time.delayedCall(500, () => {
      const finalizeWave = () => {
        this.killStreakCount = 0;
        this.maxKillStreak = 0;
        this.smartCamera.reset();

        // Victory check: all waves cleared OR all enemy bases destroyed + no enemies alive
        const allBasesGone = this.waveManager.allBasesDestroyed;
        const allWavesDone = waveNum >= this.waveManager.totalWaves;
        if (allWavesDone || allBasesGone) {
          EventBus.emit(GameEvents.GAME_OVER, true);
          SoundManager.playMusic(MusicTrack.END_CREDITS);
          return;
        }

        // Show action overlay (workshop / deploy / next wave)
        EventBus.emit(GameEvents.WAVE_TRANSITION_READY);
        this.phase = GamePhase.WORKSHOP;
        EventBus.emit(GameEvents.PHASE_CHANGED, this.phase);
      };

      // Always run repair/recharge sequence (base recovery + streak bonus)
      this.playRepairSequence(finalizeWave);
    });
  };

  private lastMiningSpark: number = 0; // throttle mining sparks

  /** Show/update HP bar when a resource is being mined */
  private onDecorMining = (data: { col: number; row: number; hp: number; maxHp: number; robotX?: number; robotY?: number }): void => {
    const key = `${data.col},${data.row}`;
    const tileX = data.col * TILE_SIZE + TILE_SIZE / 2;
    const tileY = data.row * TILE_SIZE;
    const barW = TILE_SIZE * 0.8;
    const barH = 3;

    let entry = this.resourceHpBars.get(key);
    if (!entry) {
      // Create HP bar pair (bg + fill) in world space
      const bg = this.add.rectangle(tileX, tileY - 2, barW, barH, 0x333333).setDepth(25).setOrigin(0.5, 0.5);
      const bar = this.add.rectangle(tileX, tileY - 2, barW, barH, 0x44cc44).setDepth(26).setOrigin(0.5, 0.5);
      entry = { bg, bar, fadeTimer: 2.0 };
      this.resourceHpBars.set(key, entry);
    }

    // Update bar width + color
    const ratio = Math.max(0, data.hp / data.maxHp);
    entry.bar.width = barW * ratio;
    // Green → yellow → red
    const r = ratio < 0.5 ? 255 : Math.round(255 * (1 - ratio) * 2);
    const g = ratio > 0.5 ? 255 : Math.round(255 * ratio * 2);
    entry.bar.setFillStyle(Phaser.Display.Color.GetColor(r, g, 0));
    entry.fadeTimer = 2.0; // reset fade timer — bar stays visible while being mined

    // Mining sparks — throttled to ~8 per second
    if (data.robotX != null && data.robotY != null) {
      const now = this.time.now;
      if (now - this.lastMiningSpark > 120) {
        this.lastMiningSpark = now;
        // Spark at the edge of the resource closest to the robot
        const resX = data.col * TILE_SIZE + TILE_SIZE / 2;
        const resY = data.row * TILE_SIZE + TILE_SIZE / 2;
        const dx = data.robotX - resX;
        const dy = data.robotY - resY;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const sparkX = resX + (dx / dist) * (TILE_SIZE * 0.4);
        const sparkY = resY + (dy / dist) * (TILE_SIZE * 0.4);
        JuiceEffects.particleBurst(this, sparkX, sparkY, 0xffcc44, 3, 35);
      }
    }
  };

  /** Handle decoration harvested by excavator — destroy sprite + HP bar + loot fly animation */
  private onDecorHarvested = (data: { col: number; row: number; type: string; co: number; electro: number; robotX?: number; robotY?: number }): void => {
    const tileX = data.col * TILE_SIZE + TILE_SIZE / 2;
    const tileY = data.row * TILE_SIZE + TILE_SIZE / 2;

    // Remove HP bar
    const barKey = `${data.col},${data.row}`;
    const hpBar = this.resourceHpBars.get(barKey);
    if (hpBar) {
      hpBar.bg.destroy();
      hpBar.bar.destroy();
      this.resourceHpBars.delete(barKey);
    }

    // Find and destroy the decoration image at this tile
    for (const arr of [this.decorImages, this.resImages]) {
      const idx = arr.findIndex(img => Math.abs(img.x - tileX) < 1 && Math.abs(img.y - tileY) < 1);
      if (idx >= 0) { arr[idx].destroy(); arr.splice(idx, 1); break; }
    }

    // Destroy drop shadow (e.g. crate ellipse) at same tile position
    const shadowIdx = this.decorExtras.findIndex(obj => {
      const go = obj as unknown as { x: number; y: number };
      return Math.abs(go.x - tileX) < 1 && Math.abs(go.y - (tileY + TILE_SIZE * 0.34)) < 1;
    });
    if (shadowIdx >= 0) { this.decorExtras[shadowIdx].destroy(); this.decorExtras.splice(shadowIdx, 1); }

    // Resource fly-to-HUD: one icon per unit so the player sees the loot quantity
    const cam = this.cameras.main;
    const halfW = cam.width / 2;
    const halfH = cam.height / 2;
    const screenX = (tileX - cam.scrollX - halfW) * cam.zoom + halfW;
    const screenY = (tileY - cam.scrollY - halfH) * cam.zoom + halfH;
    const spread = 6;
    for (let c = 0; c < data.co; c++) {
      const sx = screenX + (Math.random() - 0.5) * spread;
      const sy = screenY + (Math.random() - 0.5) * spread;
      EventBus.emit(GameEvents.RESOURCE_FLY_TO_HUD, { screenX: sx, screenY: sy, amount: 1, type: 'co' });
    }
    for (let c = 0; c < data.electro; c++) {
      const sx = screenX + (Math.random() - 0.5) * spread;
      const sy = screenY + (Math.random() - 0.5) * spread;
      EventBus.emit(GameEvents.RESOURCE_FLY_TO_HUD, { screenX: sx, screenY: sy, amount: 1, type: 'electro' });
    }
  };

  /** Fade out HP bars that haven't been updated recently, destroy when fully faded */
  private updateResourceHpBars(dt: number): void {
    for (const [key, entry] of this.resourceHpBars) {
      entry.fadeTimer -= dt;
      if (entry.fadeTimer <= 0) {
        // Fade out
        const alpha = Math.max(0, entry.bg.alpha - dt * 2);
        entry.bg.setAlpha(alpha);
        entry.bar.setAlpha(alpha);
        if (alpha <= 0) {
          entry.bg.destroy();
          entry.bar.destroy();
          this.resourceHpBars.delete(key);
        }
      }
    }
  }

  /** Handle mud tile conversion — update tile visuals and schedule revert if duration > 0 */
  private onMudTileConverted = (data: { tiles: { col: number; row: number; oldType: TileType }[]; duration: number; impactX: number; impactY: number }): void => {
    const darkenTint = GameScene.darkenTint(GOD.tileDarken);
    const mudTexKey = GameScene.TILE_TEXTURES[TileType.MUD];

    for (const { col, row, oldType } of data.tiles) {
      const key = `${col},${row}`;
      const oldImg = this.tileImageMap.get(key);
      if (oldImg) oldImg.destroy();

      const newImg = this.add.image(
        col * TILE_SIZE + TILE_SIZE / 2,
        row * TILE_SIZE + TILE_SIZE / 2,
        mudTexKey,
      ).setDisplaySize(TILE_SIZE, TILE_SIZE).setDepth(0).setTint(darkenTint);
      this.tileImageMap.set(key, newImg);

      // Replace in the flat array too (for tint updates)
      if (oldImg) {
        const idx = this.tileImages.indexOf(oldImg);
        if (idx >= 0) this.tileImages[idx] = newImg;
        else this.tileImages.push(newImg);
      } else {
        this.tileImages.push(newImg);
      }

      // Schedule revert if duration > 0
      if (data.duration > 0) {
        const timer = this.time.delayedCall(data.duration * 1000, () => {
          if (!this.sys.isActive()) return;
          MAP_GRID[row][col] = oldType;
          const mudImg = this.tileImageMap.get(key);
          if (mudImg) mudImg.destroy();
          const revertTexKey = GameScene.TILE_TEXTURES[oldType];
          if (!revertTexKey) return;
          const revertImg = this.add.image(
            col * TILE_SIZE + TILE_SIZE / 2,
            row * TILE_SIZE + TILE_SIZE / 2,
            revertTexKey,
          ).setDisplaySize(TILE_SIZE, TILE_SIZE).setDepth(0).setTint(GameScene.darkenTint(GOD.tileDarken));
          this.tileImageMap.set(key, revertImg);
          const midx = this.tileImages.indexOf(mudImg!);
          if (midx >= 0) this.tileImages[midx] = revertImg;
          else this.tileImages.push(revertImg);
        });
        this.mudRevertTimers.push(timer);
      }
    }
  };

  private onEnemyDestroyed = (enemy: { reward: number; body?: { x: number; y: number }; enemyType?: string; grade?: string; id?: string }): void => {
    // Remove projectiles belonging to this enemy
    if (enemy.id) this.enemyProjectileManager.removeByOwner(enemy.id);
    this.enemiesKilled++;
    // Track kills per enemy type + type:grade
    if (enemy.enemyType) {
      this.killsByType[enemy.enemyType] = (this.killsByType[enemy.enemyType] ?? 0) + 1;
      const gradeKey = `${enemy.enemyType}:${enemy.grade ?? 'normal'}`;
      this.killsByTypeGrade[gradeKey] = (this.killsByTypeGrade[gradeKey] ?? 0) + 1;
    }

    // Coin fly-to-HUD: one icon per $ so the player sees the loot quantity
    if (enemy.body && enemy.reward > 0) {
      const cam = this.cameras.main;
      const amount = Math.round(enemy.reward * GOD.resourcesMult * DIFFICULTY_REWARD_MULT);
      const halfW = cam.width / 2;
      const halfH = cam.height / 2;
      const screenX = (enemy.body.x - cam.scrollX - halfW) * cam.zoom + halfW;
      const screenY = (enemy.body.y - cam.scrollY - halfH) * cam.zoom + halfH;
      for (let c = 0; c < amount; c++) {
        // Slight random spread so coins don't stack on top of each other
        const spread = 6;
        const sx = screenX + (Math.random() - 0.5) * spread;
        const sy = screenY + (Math.random() - 0.5) * spread;
        EventBus.emit(GameEvents.COIN_FLY_TO_HUD, { screenX: sx, screenY: sy, amount: 1 });
      }
    }

    // Kill streak tracking
    this.killStreakCount++;
    if (this.killStreakCount > this.maxKillStreak) this.maxKillStreak = this.killStreakCount;
    this.killStreakTimer = GameScene.KILL_STREAK_TIMEOUT;
    if (GameScene.KILL_STREAK_THRESHOLDS.includes(this.killStreakCount) && enemy.body) {
      EventBus.emit(GameEvents.KILL_STREAK, {
        count: this.killStreakCount,
        x: enemy.body.x,
        y: enemy.body.y,
      });
      SoundManager.play('streak');
    }
  };

  private onCoinArrived = (data: { amount: number }): void => {
    this.addResources(data.amount);
  };

  private onResourceArrived = (data: { amount: number; type: 'co' | 'electro' }): void => {
    if (data.type === 'co') ResourceManager.addCo(data.amount);
    else ResourceManager.addElectro(data.amount);
  };

  /** Flag to skip the remaining repair animation (set by SPACE key) */
  private skipRepairRequested = false;

  /** Sequenced repair: camera pans to each robot, shows +HP/+EN, plays repair sound.
   *  Press SPACE to skip the animation — all remaining repairs are applied instantly. */
  private playRepairSequence(onComplete: () => void): void {
    const bonusPct = Math.min(0.20, this.maxKillStreak * GameScene.BONUS_PER_STREAK);
    const aliveRobots = this.robotManager.robots.filter(r => r.alive);
    if (aliveRobots.length === 0) { onComplete(); return; }

    this.repairSequenceActive = true;
    this.skipRepairRequested = false;

    // Listen for SPACE to skip
    const skipHandler = () => { this.skipRepairRequested = true; };
    this.input.keyboard?.on('keydown-SPACE', skipHandler);

    const cam = this.cameras.main;
    const PAN_TIME = GOD.repairPanTime;
    const SHOW_TIME = GOD.repairShowTime;
    const ZOOM = GOD.repairZoom;

    let index = 0;
    const finishSequence = () => {
      this.input.keyboard?.off('keydown-SPACE', skipHandler);
      this.repairSequenceActive = false;
      onComplete();
    };

    /** Apply repair to a single robot (no animation) */
    const applyRepairInstant = (robot: import('@/entities/Robot').Robot) => {
      robot.repairFull();
      const hpPct = GOD.waveHpRecharge + bonusPct;
      robot.currentHp = Math.min(robot.totalHp, robot.currentHp + robot.totalHp * hpPct);
      if (robot.maxEnergy > 0) {
        const enPct = GOD.waveEnergyRecharge + bonusPct;
        robot.currentEnergy = Math.min(robot.maxEnergy, robot.currentEnergy + robot.maxEnergy * enPct);
      }
    };

    const visitNext = () => {
      // Skip requested — apply all remaining repairs instantly and finish
      if (this.skipRepairRequested) {
        for (let i = index; i < aliveRobots.length; i++) {
          applyRepairInstant(aliveRobots[i]);
        }
        finishSequence();
        return;
      }

      if (index >= aliveRobots.length) {
        finishSequence();
        return;
      }

      const robot = aliveRobots[index];
      index++;

      // 0) Collect names of broken components for display, then fully repair
      const repairedNames: string[] = [];
      if (robot.isFallen) repairedNames.push('Stood up');
      const broken = robot.getBrokenComponents();
      for (const comp of broken) repairedNames.push(comp.def.name);
      robot.repairFull();

      // 2) Then apply HP + energy recovery (base + streak bonus)
      const hpBefore = robot.currentHp;
      const hpPct = GOD.waveHpRecharge + bonusPct;
      robot.currentHp = Math.min(robot.totalHp, robot.currentHp + robot.totalHp * hpPct);
      const hpGain = robot.currentHp - hpBefore;

      const energyBefore = robot.currentEnergy;
      if (robot.maxEnergy > 0) {
        const enPct = GOD.waveEnergyRecharge + bonusPct;
        robot.currentEnergy = Math.min(robot.maxEnergy, robot.currentEnergy + robot.maxEnergy * enPct);
      }
      const energyGain = robot.currentEnergy - energyBefore;

      // Pan camera to this robot
      cam.pan(robot.x, robot.y, PAN_TIME, 'Sine.easeInOut');
      cam.zoomTo(ZOOM, PAN_TIME, 'Sine.easeInOut');

      // After pan arrives, show text + sound
      this.time.delayedCall(PAN_TIME, () => {
        // Check skip again after pan
        if (this.skipRepairRequested) {
          for (let i = index; i < aliveRobots.length; i++) {
            applyRepairInstant(aliveRobots[i]);
          }
          finishSequence();
          return;
        }

        const lines: string[] = [];
        if (repairedNames.length > 0) {
          lines.push(`Repaired: ${repairedNames.join(', ')}`);
        }
        if (hpGain > 0) lines.push(`+${Math.round(hpGain)} HP`);
        if (energyGain > 0) lines.push(`+${Math.round(energyGain)} EN`);

        // Always play repair sound + heal sparkles for feedback
        SoundManager.play('repair');
        JuiceEffects.healSparkle(this, robot.x, robot.y);

        if (lines.length > 0) {
          const txt = this.add.text(robot.x, robot.y - 20, lines.join('  '), {
            fontSize: '11px', color: '#44ff88', fontFamily: 'monospace', fontStyle: 'bold',
            stroke: '#000000', strokeThickness: 2,
          }).setOrigin(0.5).setDepth(200);
          this.tweens.add({
            targets: txt, y: txt.y - 35, alpha: 0,
            duration: 800, ease: 'Cubic.easeOut',
            onComplete: () => txt.destroy(),
          });
        }

        // Wait for text to be visible, then visit next robot
        this.time.delayedCall(SHOW_TIME, visitNext);
      });
    };

    visitNext();
  }

  private onGameOver = (victory: boolean): void => {
    // Cease fire immediately — no shooting/spawning after game ends
    this.phase = GamePhase.INTERMISSION;
    // Disable all GameScene input so clicks don't pass through the overlay
    this.input.enabled = false;
    // During cinematic simulation: stop combat but skip all persistence/scoring
    if (this.registry.get('cinematicSimulation')) {
      SoundManager.stopEngineLoops();
      return;
    }
    SoundManager.stopEngineLoops();
    // Clear crash-recovery snapshot — game ended normally
    ProgressionManager.clearPreGameSnapshot();

    const levelNode = this.registry.get('currentLevelNode') as { id: string; tier: number } | undefined;
    const levelId = levelNode?.id ?? 'challenge';
    const wavesCompleted = this.waveManager?.currentWave ?? 0;

    // Finalize score via ScoreManager (single source of truth)
    const hpRatio = this.playerBase.maxHp > 0 ? this.playerBase.hp / this.playerBase.maxHp : 0;
    const fogStats = this.fogOfWar?.getExploredStats() ?? { explored: 0, total: 1 };
    const missionScore = ScoreManager.finalize(
      victory,
      hpRatio,
      this.playerBase.destroyedNodes,
      fogStats.explored,
      fogStats.total,
    );

    // Store score in registry so UIScene can read it for the debrief panel
    this.registry.set('missionScore', missionScore);

    const stars = missionScore.stars;

    // Save score
    SaveManager.addScore({
      levelId,
      date: new Date().toISOString(),
      won: victory,
      wavesCompleted,
      enemiesKilled: this.enemiesKilled,
      stars,
      totalScore: missionScore.totalScore,
      maxScore: missionScore.maxScore,
      isPlatinum: missionScore.isPlatinum,
    });

    // Persist kill counts into campaign progression
    ProgressionManager.addKills(this.killsByType, this.killsByTypeGrade);

    // Accumulate mission stats for achievements
    const liveStats = ScoreManager.getStats();
    if (liveStats) {
      ProgressionManager.addMissionStats({
        fogExplored: liveStats.fogExplored,
        robotsDeployed: liveStats.robotsDeployed,
        robotsLost: liveStats.robotsLost,
        shotsHit: liveStats.shotsHit,
        shotsFired: liveStats.shotsFired,
        basesDestroyed: liveStats.enemyBasesDestroyed,
        perfectWaves: liveStats.perfectWaves,
        bestStreak: liveStats.maxKillStreak,
        totalDistance: liveStats.totalRobotDistance,
        won: liveStats.won,
        wavesReached: liveStats.wavesReached,
        // ── Funny achievement data ──
        hpRatio: liveStats.hpRatio,
        elapsedMs: liveStats.totalWaveTimeMs,
        allRobotsDead: liveStats.robotsDeployed > 0 && liveStats.robotsLost >= liveStats.robotsDeployed,
        onlyFixedRobots: liveStats.robotsDeployed > 0 && !liveStats.hadNonFixedRobot,
        noFlyingRobots: !liveStats.hadFlyingRobot,
        onlyFlyingRobots: liveStats.hadFlyingRobot && !liveStats.hadNonFlyingRobot,
        hadSupportOnlyBot: liveStats.hadSupportOnlyBot,
        mineKills: liveStats.mineKills,
        camperBaseKills: liveStats.camperBaseKills,
        usedCheat: GOD.infiniteEnergy || GOD.infiniteHp || GOD.oneHitKill || GOD.freeBuilding,
        maxDamageSurvived: liveStats.maxDamageSurvived,
        maxSingleHitDamage: liveStats.maxSingleHitDamage,
        maxCombosOneWave: liveStats.maxCombosOneWave,
        totalRicochets: liveStats.totalRicochets,
        shotsHitPrecision: liveStats.shotsFired > 0 ? Math.round((liveStats.shotsHit / liveStats.shotsFired) * 100) : 0,
      });
    }

    // Check achievements after stats are flushed
    AchievementManager.checkAll();

    // Mark level completed in progression (campaign only)
    if (victory && levelNode) {
      // Calculate reward BEFORE completeLevel (so rerun counter is pre-increment)
      const reward = ProgressionManager.calculateVictoryReward(
        levelNode.id, stars, missionScore.isPlatinum,
      );
      ProgressionManager.completeLevel(levelNode.id, stars);
      // Victory bonus added to Treasury (base + rank bonus)
      const victoryBonus = reward.totalReward;
      this.registry.set('victoryReward', reward); // for UIScene debrief
      this.addResources(victoryBonus);

      // Rank-tier resource bonuses (Co & Electro)
      if (reward.bonusCo > 0) ResourceManager.addCo(reward.bonusCo);
      if (reward.bonusElectro > 0) ResourceManager.addElectro(reward.bonusElectro);

      // Persist cinematics that played during this level (only on victory)
      confirmPendingCompletions();

      // Auto-save deployed robot builds to the design library so they persist across maps
      for (const r of this.robotManager.robots) {
        if (r.designComponents.length === 0) continue;
        const name = r.designName || 'Robot';
        // Skip if a design with the same name already exists
        if (DesignStore.findIndexByName(name) >= 0) continue;
        DesignStore.add(name, {
          components: r.designComponents.map(c => ({
            type: c.type,
            lx: c.lx,
            ly: c.ly,
            rotation: c.rotation,
          })),
          rules: [...r.designRules],
          cost: r.designCost,
        });
      }
    }

    // Challenge victory rewards — accumulate into challenge treasury (separate economy)
    if (victory && !levelNode) {
      const wavesCleared = this.waveManager?.currentWave ?? 0;
      const baseReward = Math.round(GOD.challengeRewardBase + GOD.challengeRewardPerWave * wavesCleared);

      // Earned Co/Electro during this challenge map (fraction persists)
      const earned = ResourceManager.getEarned();
      const bonusCo = Math.round(earned.co * GOD.challengeResourceMult);
      const bonusElectro = Math.round(earned.electro * GOD.challengeResourceMult);

      // Leftover $ from this map (unspent resources)
      const surplus = Math.max(0, this.resources);

      // Accumulate into challenge treasury: reward + surplus + fraction of Co/Electro
      ResourceManager.addToChallengeTreasury(baseReward + surplus, bonusCo, bonusElectro);

      // Store reward for GameOverOverlay display
      this.registry.set('victoryReward', {
        baseReward: baseReward + surplus,
        rankBonus: 0,
        totalReward: baseReward + surplus,
        newRankTier: 0,
        prevRankTier: 0,
        bonusCo,
        bonusElectro,
        reruns: 0,
      } satisfies import('@/systems/ProgressionManager').VictoryReward);
    }
  };

  private onEnemyFired = (data: {
    sx: number; sy: number; tx: number; ty: number;
    damage: number; speed: number; color: number; range: number;
    weaponType: EnemyWeaponType;
    enemyType: string;
    enemyId?: string;
    coneAngle?: number; flameDuration?: number;
    splashRadius?: number; poisonDuration?: number; poisonDps?: number;
  }): void => {
    switch (data.weaponType) {
      case EnemyWeaponType.ACID_CATAPULT:
        this.enemyProjectileManager.spawnAcidBlob(
          data.sx, data.sy, data.tx, data.ty,
          data.damage,
          data.splashRadius ?? 45,
          data.poisonDuration ?? 5,
          data.poisonDps ?? 4,
          data.enemyId,
        );
        break;

      case EnemyWeaponType.LASER: {
        // Instant-hit beam — needs robot + base targets for immediate damage
        const beamTargets = this.robotManager.robots.filter(r => r.alive).map(r => ({
          x: r.container.x, y: r.container.y, alive: r.alive,
          takeDamage: (d: number, ax?: number, ay?: number) => r.takeDamage(d, ax, ay),
          collisionRadius: 20,
        }));
        // Add each alive base node as a beam target
        for (const bt of this.playerBase.getAliveTargets()) {
          beamTargets.push({
            x: bt.x, y: bt.y, alive: bt.alive,
            takeDamage: (d: number) => bt.takeDamage(d),
            collisionRadius: bt.collisionRadius,
          });
        }
        this.enemyProjectileManager.spawnBeam(
          data.sx, data.sy, data.tx, data.ty,
          data.damage, data.color, beamTargets,
        );
        break;
      }

      case EnemyWeaponType.FLAMETHROWER:
        this.enemyProjectileManager.spawnFlame(
          data.sx, data.sy, data.tx, data.ty,
          data.damage, data.range, data.color,
          data.coneAngle ?? Math.PI / 6,
          data.flameDuration ?? 0.8,
          data.enemyId,
        );
        break;

      default:
        // CN, CL, R (burst shot), MA — traveling projectile
        this.enemyProjectileManager.spawnProjectile(
          data.sx, data.sy, data.tx, data.ty,
          data.damage, data.speed, data.color, data.range,
          data.weaponType,
          data.enemyId,
        );
        break;
    }

    // Play enemy-specific shoot SFX
    const sfxMap: Record<string, import('@/systems/SoundManager').SfxKey> = {
      tank: 'enemyShootTank', drone: 'enemyShootDrone',
      bomber: 'enemyShootBomber', brute: 'enemyShootBrute',
      specter: 'enemyShootSpecter', hive: 'enemyShootBomber',
    };
    SoundManager.play(sfxMap[data.enemyType] ?? 'shoot');
  };

  // ── Placement helpers ──
  // Fixed ground (turrets): only PLACEMENT
  // Mobile ground (wheels): PLACEMENT + PATH
  // Flying (wings): everything except WALL

  private tileAt(col: number, row: number): TileType | null {
    if (row < 0 || row >= MAP_ROWS || col < 0 || col >= MAP_COLS) return null;
    if (this.fogOfWar && !this.fogOfWar.isTileVisible(col, row)) return null;
    if (this.crackedTiles?.isCracked(col, row)) return null;
    return MAP_GRID[row][col];
  }

  /** Whether a tile is visible (not hidden by fog of war) */
  isTileVisible(col: number, row: number): boolean {
    return !this.fogOfWar || this.fogOfWar.isTileVisible(col, row);
  }

  /** Fixed ground robot (no movement): PLACEMENT + GRILLE + BROKEN_PLACEMENT */
  isFixedPlacementTile(col: number, row: number): boolean {
    const tile = this.tileAt(col, row);
    return tile === TileType.PLACEMENT || tile === TileType.GRILLE || tile === TileType.BROKEN_PLACEMENT;
  }

  /** Mobile ground robot (wheels): PLACEMENT + GRILLE + PATH + MUD + GRASS + BROKEN_PLACEMENT */
  isMobilePlacementTile(col: number, row: number): boolean {
    const tile = this.tileAt(col, row);
    return tile === TileType.PLACEMENT || tile === TileType.GRILLE || tile === TileType.BROKEN_PLACEMENT ||
           tile === TileType.PATH || tile === TileType.MUD || tile === TileType.GROUND;
  }

  /** Flying robot (wings): everything except WALL and WATER */
  isFlyingPlacementTile(col: number, row: number): boolean {
    const tile = this.tileAt(col, row);
    if (tile === null) return false;
    return tile !== TileType.WALL && tile !== TileType.WATER;
  }

  update(time: number, delta: number): void {
    MiniProfiler.frameBegin();

    // ImpactTimeSystem modulates gameSpeed for slow-mo on big hits
    const effectiveSpeed = ImpactTimeSystem.update(delta, this.gameSpeed);
    const scaledDelta = delta * effectiveSpeed;

    // Inline counters — populated during single-pass target building, used for profiler
    let aliveRobotCount = 0;
    let aliveEnemyCount = 0;

    MiniProfiler.begin('robots');
    // Update aggro targets BEFORE enemies move so ground enemies never target flying robots
    if (this.phase === GamePhase.WAVE) {
      this.robotManager.updateAggroTargets(this.waveManager.enemies, scaledDelta / 1000);
      // Track wave phase time + peak enemies for scoring
      ScoreManager.updateWaveTime(scaledDelta);
      // Quick inline count for peak tracking (full enemy count done later in shooting block)
      let peakAlive = 0;
      for (const e of this.waveManager.enemies) { if (e.alive) peakAlive++; }
      ScoreManager.updatePeakEnemies(peakAlive);
    }
    if (this.phase === GamePhase.WAVE) {
      this.robotManager.update(this.waveManager.enemies, scaledDelta, this.waveManager.enemyHash);
      this.mineManager.update(scaledDelta, this.waveManager.enemies);
      // Base auto-turret (weak defense — enables "Mission Accomplished...?" achievement)
      this.playerBase.updateTurret(scaledDelta / 1000, this.waveManager.enemies);
    }
    MiniProfiler.end('robots');

    MiniProfiler.begin('waves');
    if (this.phase === GamePhase.WAVE) {
      this.waveManager.update(time, scaledDelta);
    }
    MiniProfiler.end('waves');

    MiniProfiler.begin('projectiles');
    // Update projectiles every frame (even between waves for lingering projectiles)
    this.projectileManager.update(scaledDelta);

    MiniProfiler.end('projectiles');

    MiniProfiler.begin('shooting');
    // Enemy shooting: enemies fire at robots (+ base when in range), enemy projectiles hit robots + base
    if (this.phase === GamePhase.WAVE) {
      const dt = scaledDelta / 1000;

      // ── Single pass over robots: build all target variants at once ──
      this._cachedRobotTargets.length = 0;
      this._cachedGroundTargets.length = 0;
      this._cachedHitTargets.length = 0;
      aliveRobotCount = 0;
      for (const r of this.robotManager.robots) {
        if (!r.alive) continue;
        aliveRobotCount++;
        const isFlying = r.canFly();
        // Shooting targets (RobotTarget shape)
        const target = {
          x: r.container.x, y: r.container.y, alive: r.alive, isFlying,
          isCamoActive: r.isCamoActive,
          getExposedTarget: () => r.getExposedTargetPosition(),
        };
        this._cachedRobotTargets.push(target);
        if (!isFlying) this._cachedGroundTargets.push(target);
        // Hit targets (EnemyProjectileManager RobotTarget shape)
        this._cachedHitTargets.push({
          x: r.container.x, y: r.container.y, alive: r.alive,
          takeDamage: (d: number, ax?: number, ay?: number) => r.takeDamage(d, ax, ay),
          collisionRadius: 20,
        });
      }

      for (const enemy of this.waveManager.enemies) {
        const baseTarget = this.playerBase.nearestAlivePosition(enemy.body.x, enemy.body.y);
        const targets = enemy.isFlyer ? this._cachedRobotTargets : this._cachedGroundTargets;
        enemy.updateShooting(dt, targets, baseTarget);
      }

      // Add base targets to hit targets
      for (const bt of this.playerBase.getAliveTargets()) {
        this._cachedHitTargets.push({
          x: bt.x, y: bt.y, alive: bt.alive,
          takeDamage: (d: number) => bt.takeDamage(d),
          collisionRadius: bt.collisionRadius,
        });
      }

      // Reuse spatial hash (clear + rebuild instead of new allocation)
      this._hitHash.rebuild(this._cachedHitTargets, r => r.x, r => r.y);

      // Build enemy targets for bounced projectiles (ricochet → enemy collision)
      this._cachedEnemyHitTargets.length = 0;
      aliveEnemyCount = 0;
      for (const e of this.waveManager.enemies) {
        if (!e.alive) continue;
        aliveEnemyCount++;
        this._cachedEnemyHitTargets.push({
          x: e.body.x, y: e.body.y, alive: e.alive,
          takeDamage: (d: number) => e.takeDamage(d),
          collisionRadius: e.collisionRadius,
        });
      }
      this.enemyProjectileManager.update(scaledDelta, this._cachedHitTargets, this._hitHash, this._cachedEnemyHitTargets);
    }
    MiniProfiler.end('shooting');

    // Kill streak timer decay
    if (this.killStreakTimer > 0) {
      this.killStreakTimer -= scaledDelta;
      if (this.killStreakTimer <= 0) {
        this.killStreakCount = 0;
      }
    }

    MiniProfiler.begin('fog');
    // Fog of war: update explored tiles + enemy visibility
    if (this.phase === GamePhase.WAVE || this.game.getFrame() % 30 === 0) {
      this.fogOfWar?.update(this.robotManager.robots, this.waveManager.enemies, scaledDelta, this.waveManager.enemyBases);
    }
    MiniProfiler.end('fog');

    MiniProfiler.begin('camera');
    this.updateDynamicCamera(delta);
    SlowmoFXManager.update();
    MiniProfiler.end('camera');

    MiniProfiler.begin('culling');
    // Viewport culling for overlays during non-WAVE
    if (this.phase !== GamePhase.WAVE && this.game.getFrame() % 60 === 0) {
      for (const robot of this.robotManager.robots) {
        if (robot.alive) robot.updateDebugOverlays();
      }
    }
    MiniProfiler.end('culling');

    if (this.phase === GamePhase.WAVE) {
      this.updateEngineSounds();
    }

    // Fade out resource HP bars when not being mined
    this.updateResourceHpBars(delta / 1000);

    // Profiler counters (use inline counters from WAVE pass when available, else count here)
    if (this.phase !== GamePhase.WAVE) {
      for (const r of this.robotManager.robots) { if (r.alive) aliveRobotCount++; }
      for (const e of this.waveManager.enemies) { if (e.alive) aliveEnemyCount++; }
    }
    MiniProfiler.set('robots', aliveRobotCount);
    MiniProfiler.set('enemies', aliveEnemyCount);
    MiniProfiler.set('gameObjs', this.children.length);
    MiniProfiler.set('tweens', this.tweens.getTweens().length);
    MiniProfiler.set('phase', this.phase === GamePhase.WAVE ? 1 : 0);

    MiniProfiler.frameEnd();
  }

  /** Smoothly adjust camera zoom and position to frame the action */
  private updateDynamicCamera(delta: number): void {
    if (this.repairSequenceActive) return; // camera controlled by repair pan
    if (this.registry.get('tutorialCameraLock')) return; // camera controlled by tutorial

    const dt = delta / 1000;
    const isManualPhase = this.phase !== GamePhase.WAVE;

    // ── Manual pan: keyboard + drag-pan (all phases) ──
    {
      const panStep = GameScene.PAN_SPEED * dt;
      if (this.panKeys.up)    this.manualOffsetY -= panStep;
      if (this.panKeys.down)  this.manualOffsetY += panStep;
      if (this.panKeys.left)  this.manualOffsetX -= panStep;
      if (this.panKeys.right) this.manualOffsetX += panStep;
    }

    // ── Camera lock-on: follow selected robot ──
    if (this.cameraFollowRobot) {
      if (!this.cameraFollowRobot.alive) {
        // Robot died — release lock-on
        this.cameraFollowRobot = null;
        this.cameras.main.roundPixels = true; // restore pixel snapping
        this.manualZoomStep = 10; // reset to neutral zoom
      } else {
        this.camTargetX = this.cameraFollowRobot.x + this.manualOffsetX;
        this.camTargetY = this.cameraFollowRobot.y + this.manualOffsetY;
        // Apply manual zoom step on top of follow zoom — allows player to zoom in/out while following
        const manualMult = GameScene.ZOOM_STEPS[this.manualZoomStep]; // 1.0 at neutral (index 10)
        this.camTargetZoom = Math.max(GOD.cameraFollowZoom * manualMult, 0.5);

        // Frame-independent exponential smoothing: same visual result at any FPS
        const dtSec = delta / 1000;
        const followLerp = GOD.cameraFollowLerp;
        const posAlpha = 1 - Math.pow(0.001, followLerp * dtSec);
        const zoomAlpha = 1 - Math.pow(0.001, followLerp * 0.75 * dtSec);

        const dx = this.camTargetX - this.camCurrentX;
        const dy = this.camTargetY - this.camCurrentY;
        const dz = this.camTargetZoom - this.camCurrentZoom;

        // Snap when close enough — prevents sub-pixel jitter from never-converging lerp
        if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) {
          this.camCurrentX = this.camTargetX;
          this.camCurrentY = this.camTargetY;
        } else {
          this.camCurrentX += dx * posAlpha;
          this.camCurrentY += dy * posAlpha;
        }
        this.camCurrentZoom += dz * zoomAlpha;

        const cam = this.cameras.main;
        // Disable pixel rounding during follow — prevents 1px stutter on slow moves
        cam.roundPixels = false;
        cam.setZoom(this.camCurrentZoom);
        cam.centerOn(this.camCurrentX, this.camCurrentY);
        return; // skip SmartCamera / workshop camera
      }
    }

    if (this.phase === GamePhase.WAVE && this.waveManager.enemies.length > 0) {
      // During waves: follow action density via SmartCamera
      const target = this.smartCamera.computeTarget(
        this.waveManager.enemies,
        this.robotManager.robots,
        dt,
        (px, py) => this.fogOfWar ? this.fogOfWar.isPositionVisible(px, py) : true,
      );
      // Apply manual zoom multiplier on top of SmartCamera zoom
      const zoomMult = GameScene.ZOOM_STEPS[this.manualZoomStep] ?? 1.0;
      this.camTargetZoom = target.zoom * zoomMult;
      // Apply manual pan offset on top of SmartCamera position
      this.camTargetX = target.x + this.manualOffsetX;
      this.camTargetY = target.y + this.manualOffsetY;
    } else {
      // Workshop / Deploy: frame revealed fog area + manual offsets
      // No fog → frame the full map
      const bounds = this.fogOfWar
        ? this.fogOfWar.getRevealedBounds()
        : { x: 0, y: 0, w: MAP_COLS * TILE_SIZE, h: MAP_ROWS * TILE_SIZE };
      if (!bounds) return;

      const PADDING = TILE_SIZE * 1.5;
      const padX = bounds.x - PADDING;
      const padY = bounds.y - PADDING;
      const padW = bounds.w + PADDING * 2;
      const padH = bounds.h + PADDING * 2;

      const zoomX = CANVAS_WIDTH / padW;
      const zoomY = CANVAS_HEIGHT / padH;
      const minZoom = Math.min(CANVAS_WIDTH / GAME_WIDTH, CANVAS_HEIGHT / GAME_HEIGHT);
      const autoZoom = Math.max(minZoom, Math.min(5, Math.min(zoomX, zoomY))) * GOD.cameraZoomMult;

      // Apply manual zoom multiplier
      const zoomMult = GameScene.ZOOM_STEPS[this.manualZoomStep] ?? 1.0;
      this.camTargetZoom = autoZoom * zoomMult;

      // Apply manual pan offset
      this.camTargetX = padX + padW / 2 + this.manualOffsetX;
      this.camTargetY = padY + padH / 2 + this.manualOffsetY;

      // Soft-clamp: keep camera center within the map rect so action
      // on edges can be centered (black padding is fine beyond map).
      this.camTargetX = Math.max(0, Math.min(GAME_WIDTH, this.camTargetX));
      this.camTargetY = Math.max(0, Math.min(GAME_HEIGHT, this.camTargetY));

      // Clamp offset so it can't push beyond bounds
      const autoCX = padX + padW / 2;
      const autoCY = padY + padH / 2;
      this.manualOffsetX = this.camTargetX - autoCX;
      this.manualOffsetY = this.camTargetY - autoCY;
    }

    // Smooth lerp toward target — faster during manual control, slower during wave auto-cam
    const lerpMult = isManualPhase ? 6.0 : 1.0;
    const posLerp = Math.min(1, 1.2 * lerpMult * delta / 1000);
    const zoomLerp = Math.min(1, 0.8 * lerpMult * delta / 1000);
    this.camCurrentX += (this.camTargetX - this.camCurrentX) * posLerp;
    this.camCurrentY += (this.camTargetY - this.camCurrentY) * posLerp;
    this.camCurrentZoom += (this.camTargetZoom - this.camCurrentZoom) * zoomLerp;

    // Apply to camera
    const cam = this.cameras.main;
    cam.setZoom(this.camCurrentZoom);
    cam.centerOn(this.camCurrentX, this.camCurrentY);

  }

  /** Build lightweight entity list and feed it to SoundManager engine loops */
  private updateEngineSounds(): void {
    const entities: EngineSoundEntity[] = [];

    // Robots: wheels → robotWheel, wings/turbine → robotFly
    for (const r of this.robotManager.robots) {
      if (!r.alive) continue;
      const hasWheel = r.components.some(c => !c.broken && c.def.type === ComponentType.WHEEL);
      const hasWing = r.canFly();
      if (hasWheel) entities.push({ x: r.x, y: r.y, alive: true, voice: 'robotWheel' });
      if (hasWing) entities.push({ x: r.x, y: r.y, alive: true, voice: 'robotFly' });
    }

    // Enemies: mechanical ground/fly only
    for (const e of this.waveManager.enemies) {
      if (!e.alive) continue;
      if (!MECHANICAL_ENEMY_TYPES.has(e.enemyType)) continue;
      const isFlyer = FLYING_ENEMY_TYPES.has(e.enemyType);
      entities.push({
        x: e.body.x, y: e.body.y, alive: true,
        voice: isFlyer ? 'enemyFly' : 'enemyGround',
      });
    }

    SoundManager.updateEngineLoops(entities, this.camCurrentX, this.camCurrentY);
  }

  shutdown(): void {
    UITargetRegistry.unregister('placementTile');
    // Tile multi-targets
    UITargetRegistry.unregisterMulti('allPlacementTiles');
    UITargetRegistry.unregisterMulti('allPathTiles');
    UITargetRegistry.unregisterMulti('allWalls');
    UITargetRegistry.unregisterMulti('allSpecialTiles');
    UITargetRegistry.unregisterMulti('allBrokenPlacements');
    // Entity multi-targets
    UITargetRegistry.unregisterMulti('allEnemies');
    UITargetRegistry.unregisterMulti('allRobots');
    UITargetRegistry.unregisterMulti('allBases');
    UITargetRegistry.unregisterMulti('allSpawns');
    for (const et of [
      EnemyType.GRUNT, EnemyType.RUNNER, EnemyType.TANK, EnemyType.SWARM,
      EnemyType.BRUTE, EnemyType.PHANTOM, EnemyType.DRONE, EnemyType.BOMBER,
      EnemyType.WASP, EnemyType.SPECTER,
    ]) {
      UITargetRegistry.unregisterMulti(`all${et.charAt(0).toUpperCase() + et.slice(1)}s`);
    }
    // Conditions
    ConditionRegistry.unregister('noWalls');
    ConditionRegistry.unregister('noBrokenPlacements');
    ConditionRegistry.unregister('noSpecialTiles');
    ConditionRegistry.unregister('noEnemies');
    ConditionRegistry.unregister('noRobots');
    // Restore persisted campaign resources when leaving challenge mode
    if (!this.isCampaign) {
      ResourceManager.exitTemporary();
    }
    SoundManager.stopEngineLoops();
    // Cleanup resource HP bars
    for (const entry of this.resourceHpBars.values()) {
      entry.bg.destroy();
      entry.bar.destroy();
    }
    this.resourceHpBars.clear();
    this.isDraggingCamera = false;
    // Rollback cinematics that played but level wasn't completed (quit/lost)
    rollbackPendingCompletions();
    this.waveManager.shutdown();
    this.robotManager.shutdown();
    this.playerBase.destroy();
    EventBus.off(GameEvents.ENEMY_REACHED_BASE, this.onEnemyReachedBase, this);
    EventBus.off(GameEvents.WAVE_COMPLETED, this.onWaveCompleted, this);
    EventBus.off(GameEvents.ENEMY_DESTROYED, this.onEnemyDestroyed, this);
    EventBus.off(GameEvents.GAME_OVER, this.onGameOver, this);
    EventBus.off(GameEvents.ENEMY_FIRED, this.onEnemyFired, this);
    EventBus.off(GameEvents.COIN_ARRIVED, this.onCoinArrived, this);
    EventBus.off(GameEvents.RESOURCE_ARRIVED, this.onResourceArrived, this);
    EventBus.off(GameEvents.GOD_PARAMS_CHANGED, this.applyVisualFX, this);
    EventBus.off(GameEvents.DECOR_MINING, this.onDecorMining, this);
    EventBus.off(GameEvents.DECOR_HARVESTED, this.onDecorHarvested, this);
    if (this.onRobotDestroyedCleanup) {
      EventBus.off(GameEvents.ROBOT_DESTROYED, this.onRobotDestroyedCleanup);
      this.onRobotDestroyedCleanup = null;
    }
    EventBus.off(GameEvents.ROBOT_SELECTED);
    this.cameraFollowRobot = null;
    for (const img of this.tileImages) img.destroy();
    for (const img of this.decorImages) img.destroy();
    for (const obj of this.decorExtras) obj.destroy();
    for (const img of this.resImages) img.destroy();
    this.tileImages = [];
    this.tileImageMap.clear();
    for (const timer of this.mudRevertTimers) timer.destroy();
    this.mudRevertTimers = [];
    this.decorImages = [];
    this.decorExtras = [];
    this.resImages = [];
    this.projectileManager.clear();
    this.enemyProjectileManager.clear();
    this.mineManager.shutdown();
    this.cpuPlayer.shutdown();
    this.fogOfWar?.shutdown();
    this.crackedTiles?.shutdown();
    this.smartCamera.shutdown();
    ImpactTimeSystem.shutdown();
    SlowmoFXManager.shutdown();
    JuiceEffects.disconnectEvents();
    VFXPool.shutdown();
  }
}
